// Connexion Google "zero-token-until-challenge" — pendant de
// passwordSecureAuthService.ts côté Google. Contrairement à GoogleButtonAlt.tsx
// (signInWithPopup classique : le SDK Firebase émet un ID token complet dès la
// popup fermée, AVANT toute vérification appareil/2FA — voir
// proxy-server/routes/googleSecureAuth.js pour le détail de la faille que ça
// ouvre), ce service ne touche JAMAIS le SDK Firebase Auth avant que le
// serveur ait validé l'identité Google ET, si requis, le code appareil / le
// code 2FA. signInWithCustomToken n'est appelé qu'une fois tout résolu —
// avant ça, aucun token Firebase n'existe côté client, point.
//
// Flow :
//   1. Google Identity Services (popup OAuth "code flow", pas signInWithPopup)
//      → un code d'autorisation à usage unique, inexploitable sans le client
//      secret qui reste sur le serveur.
//   2. POST /api/auth/google/start — le serveur échange le code, vérifie
//      l'identité, décide si un appareil non reconnu et/ou une 2FA TOTP
//      doivent être confirmés.
//   3. Si rien à confirmer → customToken renvoyé direct. Sinon → pendingSessionId
//      opaque + resolveDeviceChallenge / resolveTotpChallenge jusqu'à résolution
//      complète — voir SecureChallengeModal.tsx.
import type { User } from 'firebase/auth';
import { getDeviceId, getCurrentDeviceInfo } from './deviceService';
import { postJson, finishWithCustomToken, toChallengeOutcome, type AuthChallenge, type ChallengeOutcome } from './secureAuthShared';

// Client OAuth Google (PUBLIC — aucun risque à l'exposer, comme la Firebase
// apiKey dans src/lib/firebase.ts). C'est le même client que celui utilisé par
// signInWithPopup ailleurs dans l'app : Firebase Console > Authentication >
// Sign-in method > Google > "Web SDK configuration". Le client SECRET associé
// (jamais exposé ici) va dans proxy-server/.env sous GOOGLE_OAUTH_CLIENT_SECRET.
export const GOOGLE_OAUTH_CLIENT_ID = '213088075878-dehqu1rdkl37lur39b7e9nbdmejvqe7e.apps.googleusercontent.com';

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

interface GisCodeResponse {
  code?: string;
  error?: string;
}

interface GisCodeClient {
  requestCode: () => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initCodeClient: (config: {
            client_id: string;
            scope: string;
            ux_mode: 'popup' | 'redirect';
            callback: (response: GisCodeResponse) => void;
            error_callback?: (error: { type: string; message?: string }) => void;
          }) => GisCodeClient;
        };
      };
    };
  }
}

let gisLoadPromise: Promise<void> | null = null;

function loadGoogleIdentityServices(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (gisLoadPromise) return gisLoadPromise;

  gisLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SCRIPT_SRC}"]`);
    if (existing) {
      if (window.google?.accounts?.oauth2) { resolve(); return; }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Impossible de charger la connexion Google.')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Impossible de charger la connexion Google. Vérifie ta connexion internet.'));
    document.head.appendChild(script);
  });
  return gisLoadPromise;
}

function requestAuthorizationCode(): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!window.google?.accounts?.oauth2) {
      reject(new Error('Google Identity Services indisponible'));
      return;
    }
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: GOOGLE_OAUTH_CLIENT_ID,
      scope: 'openid email profile',
      ux_mode: 'popup',
      callback: (response) => {
        if (response?.code) resolve(response.code);
        else reject(new Error('Connexion Google annulée.'));
      },
      error_callback: (error) => {
        if (error?.type === 'popup_closed') reject(new Error('Connexion annulée.'));
        else reject(new Error('Popup Google bloquée ou refusée. Autorise les popups et réessaie.'));
      },
    });
    client.requestCode();
  });
}

export type GoogleStartOutcome =
  | { status: 'authenticated'; user: User; isNewUser: boolean; onboardingCompleted: boolean }
  | { status: 'challenge'; pendingSessionId: string; challenge: AuthChallenge };

/** Déclenche la popup Google puis /api/auth/google/start. Ne signe jamais dans Firebase directement. */
export async function startGoogleSecureLogin(): Promise<GoogleStartOutcome> {
  await loadGoogleIdentityServices();
  const code = await requestAuthorizationCode();

  const deviceId = getDeviceId();
  const info = getCurrentDeviceInfo();

  const data = await postJson<any>('/api/auth/google/start', {
    code,
    deviceId,
    label: info.label,
    browser: info.browser,
    os: info.os,
    deviceType: info.deviceType,
    model: info.model,
  });

  if (data.status === 'authenticated') {
    const user = await finishWithCustomToken(data.customToken);
    return { status: 'authenticated', user, isNewUser: !!data.isNewUser, onboardingCompleted: data.onboardingCompleted !== false };
  }

  return {
    status: 'challenge',
    pendingSessionId: data.pendingSessionId,
    challenge: {
      requiresDevice: !!data.requiresDevice,
      requiresTotp: !!data.requiresTotp,
      cooldownSeconds: data.cooldownSeconds,
    },
  };
}

export async function resolveDeviceChallenge(pendingSessionId: string, code: string): Promise<ChallengeOutcome> {
  const data = await postJson<any>('/api/auth/google/verify-device', { pendingSessionId, code });
  return toChallengeOutcome(data);
}

export async function resolveTotpChallenge(pendingSessionId: string, code: string): Promise<ChallengeOutcome> {
  const data = await postJson<any>('/api/auth/google/verify-totp', { pendingSessionId, code });
  return toChallengeOutcome(data);
}

export async function resendGoogleDeviceCode(pendingSessionId: string): Promise<{ cooldownSeconds: number }> {
  return postJson<{ cooldownSeconds: number }>('/api/auth/google/resend-device-code', { pendingSessionId });
}
